import { useEffect, useState } from "react";
import { collection, deleteDoc, doc, getDocs, orderBy, query, updateDoc } from "firebase/firestore";
import { db, auth } from "../../firebase/config";
import { createCmsDoc, uploadCmsImage, inputClass, Field } from "../cmsApi.jsx";
import CmsWithMediaSpecs from "../components/CmsMediaSpecsPanel.jsx";
import {
  CMS_ICON_SPECS,
  validateCmsIconFile,
  BRAND_ICON_OPTIONS,
  BRAND_ICON_CATEGORIES,
  LABSYSO_BRAND_ICON_OPTIONS,
  CMS_ICON_CLASSIFICATIONS,
  CMS_ICON_CLASSIFICATION_HINTS,
  DEFAULT_CMS_ICON_CLASSIFICATION,
  normalizeCmsIconClassification,
} from "../../lib/cmsIconCatalog";
import { LUCIDE_ICON_OPTIONS, REACT_ICON_OPTIONS } from "../../lib/libraryIcons";
import { SOCIAL_PRESET_OPTIONS } from "../../lib/socialPlatforms";
import { Link } from "react-router-dom";
import { BrandIcon } from "../../components/BrandIcon";

/** Hover/focus: agranda icono y etiqueta. */
const TILE =
  "group flex items-center gap-3 border border-brand-soft rounded-2xl p-3 outline-none transition-all duration-200 hover:border-brand-primary/40 hover:bg-brand-soft/25 focus-visible:border-brand-primary focus-visible:ring-2 focus-visible:ring-brand-primary/25 focus-visible:bg-brand-soft/25";
const TILE_ICON =
  "w-10 h-10 rounded-xl bg-brand-soft/50 flex items-center justify-center shrink-0 transition-transform duration-200 ease-out group-hover:scale-125 group-focus-visible:scale-125";
const TILE_LABEL =
  "text-xs font-bold text-brand-dark leading-tight transition-transform duration-200 ease-out origin-left group-hover:scale-110 group-focus-visible:scale-110";
const TILE_COMPACT =
  "group flex flex-col items-center gap-1 border border-brand-soft rounded-xl p-2 text-center outline-none transition-all duration-200 hover:border-brand-primary/40 hover:bg-brand-soft/25 focus-visible:border-brand-primary focus-visible:ring-2 focus-visible:ring-brand-primary/25";
const TILE_COMPACT_ICON =
  "transition-transform duration-200 ease-out group-hover:scale-125 group-focus-visible:scale-125 text-brand-icon";
const TILE_COMPACT_LABEL =
  "text-[9px] font-bold text-brand-dark/70 line-clamp-2 leading-tight transition-transform duration-200 ease-out group-hover:scale-110 group-focus-visible:scale-110 group-hover:text-brand-dark group-focus-visible:text-brand-dark";

export default function CmsIconsEditor() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [classification, setClassification] = useState(DEFAULT_CMS_ICON_CLASSIFICATION);
  const [filterClass, setFilterClass] = useState("all");
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      try {
        const q = query(collection(db, "cmsIcons"), orderBy("name", "asc"));
        const snap = await getDocs(q);
        setItems(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      } catch {
        const snap = await getDocs(collection(db, "cmsIcons"));
        setItems(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      }
    } catch (err) {
      setItems([]);
      setError(
        err?.code === "permission-denied" || /permission/i.test(err?.message || "")
          ? "Sin permiso para leer cmsIcons. Despliegue firestore.rules desde website/ (firebase deploy --only firestore:rules)."
          : err?.message || "No se pudo cargar el catálogo."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const err = validateCmsIconFile(file);
    if (err) {
      setError(err);
      return;
    }
    setUploading(true);
    setError("");
    setMessage("");
    try {
      const url = await uploadCmsImage("icons", file);
      const finalName =
        name.trim() ||
        file.name.replace(/\.[^.]+$/, "").replace(/[_-]+/g, " ").trim() ||
        "Icono";
      await createCmsDoc("cmsIcons", {
        name: finalName,
        url,
        classification: normalizeCmsIconClassification(classification),
        updatedBy: auth.currentUser?.uid || null,
      });
      setName("");
      setMessage("Icono añadido al catálogo.");
      await load();
    } catch (err2) {
      setError(
        err2?.message ||
          "Error al subir. Verifique que Firebase Storage esté activo y las reglas desplegadas."
      );
    } finally {
      setUploading(false);
    }
  };

  const rename = async (id, nextName) => {
    await updateDoc(doc(db, "cmsIcons", id), { name: nextName });
    await load();
  };

  const setItemClass = async (id, nextClass) => {
    await updateDoc(doc(db, "cmsIcons", id), {
      classification: normalizeCmsIconClassification(nextClass),
    });
    await load();
  };

  const remove = async (id) => {
    if (!window.confirm("Â¿Eliminar este icono del catálogo? (el archivo en Storage puede quedar huérfano)")) {
      return;
    }
    await deleteDoc(doc(db, "cmsIcons", id));
    await load();
  };

  const filteredItems =
    filterClass === "all"
      ? items
      : items.filter(
          (i) => normalizeCmsIconClassification(i.classification || i.category) === filterClass
        );

  return (
    <div className="space-y-8">
      <div>
        <h2 className="font-display text-3xl font-black">Catálogo de iconos</h2>
        <p className="text-sm text-brand-dark/55 mt-2 max-w-3xl leading-relaxed">
          Hay dos capas distintas:
        </p>
        <ul className="text-sm text-brand-dark/55 mt-2 max-w-3xl list-disc pl-5 space-y-1 leading-relaxed">
          <li>
            <strong>Fuentes del sistema</strong> (solo lectura / referencia): Presets redes, Marca
            AGDYSA, Marca LABSYSO, Lucide y React Icons. No se "sube" a esas listas.
          </li>
          <li>
            <strong>Iconos personalizados subidos</strong>: aquí van los archivos del cliente. La
            clasificación (Redes, Menú responsivo, AGDYSA, LABSYSO, etc.) es solo una etiqueta para
            filtrarlos en los editores - no mueve el icono a Presets / Marca / Lucide.
          </li>
        </ul>
      </div>

          <section className="bg-white rounded-[28px] border border-brand-soft p-6 space-y-4">
            <h3 className="font-bold text-lg">Subir icono personalizado</h3>
            <p className="text-sm text-brand-dark/55">
              El archivo queda siempre en "Iconos personalizados subidos". Elija la etiqueta según
              dónde lo usará (Footer, menú, marca propia AGDYSA/LABSYSO, etc.). Las secciones "Iconos
              de marca" de arriba son el paquete oficial (solo lectura); archivos nuevos de marca del
              cliente van con etiqueta AGDYSA o LABSYSO.
            </p>
            <CmsWithMediaSpecs variant="icons">
            <Field label="Nombre (opcional)">
              <input
                className={inputClass}
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Ej. Logo X / Twitter"
              />
            </Field>
            <Field label="Etiqueta de uso (solo dentro de «Subidos»)">
              <select
                className={inputClass}
                value={classification}
                onChange={(e) => setClassification(e.target.value)}
              >
                {Object.entries(CMS_ICON_CLASSIFICATIONS).map(([key, title]) => (
                  <option key={key} value={key}>
                    {title}
                  </option>
                ))}
              </select>
              <p className="text-xs text-brand-dark/45 mt-2 leading-relaxed">
                {CMS_ICON_CLASSIFICATION_HINTS[classification] || ""}
              </p>
            </Field>
            <label className="inline-flex items-center gap-2 bg-brand-primary text-white px-5 py-3 rounded-xl text-xs font-bold uppercase tracking-widest cursor-pointer hover:bg-brand-dark">
              {uploading ? "Subiendoâ€¦" : "Seleccionar archivo"}
              <input
                type="file"
                accept={CMS_ICON_SPECS.accept}
                className="hidden"
                disabled={uploading}
                onChange={onUpload}
              />
            </label>
            {error && <p className="text-sm text-red-600">{error}</p>}
            {message && <p className="text-sm text-brand-primary font-medium">{message}</p>}
            <p className="text-xs text-brand-dark/45">
              Luego úselo en{" "}
              <Link to="/admin/footer" className="text-brand-primary font-bold underline">
                Footer
              </Link>
              ,{" "}
              <Link to="/admin/navigation" className="text-brand-primary font-bold underline">
                Menú responsivo
              </Link>{" "}
              u otros editores (pestaña Subidos).
            </p>
            </CmsWithMediaSpecs>
          </section>

      <section className="bg-white rounded-[28px] border border-brand-soft p-6 space-y-4">
        <div>
          <h3 className="font-bold text-lg">
            Presets redes ({SOCIAL_PRESET_OPTIONS.length}) - fuente del sistema
          </h3>
          <p className="text-sm text-brand-dark/55 mt-1">
            No se suben aquí. Para un logo propio de red, súbalo abajo con etiqueta{" "}
            <strong>Redes sociales</strong> (queda en Subidos y en el selector del Footer).
          </p>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
          {SOCIAL_PRESET_OPTIONS.map((item) => {
            const Comp = item.Component;
            return (
              <div key={item.key} tabIndex={0} className={TILE}>
                <span className={TILE_ICON}>
                  <Comp size={22} className="text-brand-icon" />
                </span>
                <span className={TILE_LABEL}>{item.label}</span>
              </div>
            );
          })}
        </div>
      </section>

      <section className="bg-white rounded-[28px] border border-brand-soft p-6 space-y-6">
        <div>
          <h3 className="font-bold text-lg">
            Iconos de marca AGDYSA ({BRAND_ICON_OPTIONS.length}) - fuente del sistema
          </h3>
          <p className="text-sm text-brand-dark/55 mt-1">
            Paquete oficial `@agdysa/icons` (solo lectura). Archivos nuevos de marca del cliente:
            súbalos abajo con etiqueta <strong>AGDYSA</strong> (quedan en Subidos).
          </p>
        </div>
        {Object.entries(BRAND_ICON_CATEGORIES).map(([cat, title]) => {
          const list = BRAND_ICON_OPTIONS.filter((i) => i.category === cat);
          if (!list.length) return null;
          return (
            <div key={cat}>
              <p className="text-[10px] font-bold uppercase tracking-widest text-brand-dark/40 mb-3">
                {title}
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                {list.map((item) => (
                  <div key={item.key} tabIndex={0} className={TILE}>
                    <span className={TILE_ICON}>
                      <BrandIcon id={item.brandId} size={28} />
                    </span>
                    <span className={TILE_LABEL}>
                      {item.label}
                      {item.pending ? " *" : ""}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </section>

      <section className="bg-white rounded-[28px] border border-brand-soft p-6 space-y-4">
        <div>
          <h3 className="font-bold text-lg">
            Iconos de marca LABSYSO ({LABSYSO_BRAND_ICON_OPTIONS.length}) - fuente del sistema
          </h3>
          <p className="text-sm text-brand-dark/55 mt-1">
            Subconjunto del paquete de marca. Archivos propios del lab: súbalos con etiqueta{" "}
            <strong>LABSYSO</strong> (quedan en Subidos).
          </p>
        </div>
        {LABSYSO_BRAND_ICON_OPTIONS.length === 0 ? (
          <p className="text-sm text-brand-dark/50">No hay iconos etiquetados LABSYSO aún.</p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            {LABSYSO_BRAND_ICON_OPTIONS.map((item) => (
              <div key={item.key} tabIndex={0} className={TILE}>
                <span className={TILE_ICON}>
                  <BrandIcon id={item.brandId} size={28} />
                </span>
                <span className={TILE_LABEL}>
                  {item.label}
                  {item.pending ? " *" : ""}
                </span>
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="bg-white rounded-[28px] border border-brand-soft p-6 space-y-4">
        <h3 className="font-bold text-lg">
          Librerías del proyecto (Lucide {LUCIDE_ICON_OPTIONS.length} + React Icons{" "}
          {REACT_ICON_OPTIONS.length}) - fuente del sistema
        </h3>
        <p className="text-sm text-brand-dark/55">
          No equivalen a la etiqueta «Contenido / páginas». Son librerías técnicas fijas (pestañas
          Lucide / React Icons del selector). Valores:{" "}
          <code className="text-xs">lucide:Nombre</code> o <code className="text-xs">ri:Nombre</code>.
        </p>
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2 max-h-64 overflow-y-auto">
          {[...LUCIDE_ICON_OPTIONS.slice(0, 24), ...REACT_ICON_OPTIONS].map((item) => {
            const Comp = item.Component;
            return (
              <div key={item.key} tabIndex={0} className={TILE_COMPACT} title={item.key}>
                <Comp size={22} className={TILE_COMPACT_ICON} />
                <span className={TILE_COMPACT_LABEL}>{item.label}</span>
              </div>
            );
          })}
        </div>
      </section>

      <section className="bg-white rounded-[28px] border border-brand-soft p-6 space-y-4">
        <div>
          <h3 className="font-bold text-lg">
            Iconos personalizados subidos ({items.length})
          </h3>
          <p className="text-sm text-brand-dark/55 mt-1">
            Única lista donde caen los archivos subidos. Los filtros son etiquetas de uso (donde se
            usará el icono), no copias de Presets / Marca / Lucide.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => setFilterClass("all")}
            className={`px-2 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest ${
              filterClass === "all" ? "bg-brand-dark text-white" : "bg-white border border-brand-soft"
            }`}
          >
            Todas
          </button>
          {Object.entries(CMS_ICON_CLASSIFICATIONS).map(([key, title]) => (
            <button
              key={key}
              type="button"
              onClick={() => setFilterClass(key)}
              className={`px-2 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest ${
                filterClass === key ? "bg-brand-dark text-white" : "bg-white border border-brand-soft"
              }`}
            >
              {title}
            </button>
          ))}
        </div>
        {filterClass !== "all" && CMS_ICON_CLASSIFICATION_HINTS[filterClass] && (
          <p className="text-xs text-brand-dark/45">{CMS_ICON_CLASSIFICATION_HINTS[filterClass]}</p>
        )}
        {loading && <p className="text-sm text-brand-dark/50">Cargandoâ€¦</p>}
        {!loading && filteredItems.length === 0 && (
          <p className="text-sm text-brand-dark/50">
            No hay iconos subidos con esta etiqueta. Los Presets / Marca / Lucide viven en las
            secciones de arriba (fuente del sistema).
          </p>
        )}
        <div className="grid sm:grid-cols-2 gap-3">
          {filteredItems.map((item) => (
            <div
              key={item.id}
              tabIndex={0}
              className="group flex flex-col gap-2 border border-brand-soft rounded-2xl p-3 outline-none transition-all duration-200 hover:border-brand-primary/40 hover:bg-brand-soft/25 focus-within:border-brand-primary focus-within:ring-2 focus-within:ring-brand-primary/25"
            >
              <div className="flex items-center gap-3">
                <img
                  src={item.url}
                  alt={item.name}
                  className="w-12 h-12 object-contain shrink-0 transition-transform duration-200 ease-out group-hover:scale-125 group-focus-within:scale-125"
                />
                <input
                  className={`${inputClass} flex-1 transition-transform duration-200 ease-out group-hover:scale-[1.02] group-focus-within:scale-[1.02] origin-left`}
                  defaultValue={item.name}
                  onBlur={(e) => {
                    const next = e.target.value.trim();
                    if (next && next !== item.name) rename(item.id, next);
                  }}
                />
                <button
                  type="button"
                  onClick={() => remove(item.id)}
                  className="text-xs font-bold text-red-600 shrink-0"
                >
                  Eliminar
                </button>
              </div>
              <select
                className={inputClass}
                value={normalizeCmsIconClassification(item.classification || item.category)}
                onChange={(e) => setItemClass(item.id, e.target.value)}
              >
                {Object.entries(CMS_ICON_CLASSIFICATIONS).map(([key, title]) => (
                  <option key={key} value={key}>
                    {title}
                  </option>
                ))}
              </select>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
